import { c as _c } from "react/compiler-runtime";
import type { ItemMaterial, MaterialPublicDto, ProductPublicDto } from "@sticker-giant/sg-frontend-common";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { trackMaterialSelected } from "@/lib/analytics/segment";
import { getEntry } from "@/lib/contentful/guards";
import { isValidOption } from "@/lib/product/validation";
import { useProduct } from "@/queries/useProduct";
import { usePdpStore } from "@/stores/usePdpStore";
import type { TypeMaterial } from "@/types/contentful/generated";
import OptionLabel from "./OptionLabel";
import { useProductBuilderContext } from "./ProductBuilderContext";
import { useProductMicrocopy } from "./useProductMicrocopy";
import ValidationError, { useFieldErrorId } from "./ValidationError";
function clearDependentSelectionIfInvalid(currentSelection: string | undefined, availableOptions: {
  sku: string;
}[], clearSelection: () => void) {
  if (currentSelection && !isValidOption(currentSelection, availableOptions)) {
    clearSelection();
  }
}
function handleMaterialChange(material: ItemMaterial, productAdmin: ProductPublicDto) {
  const store = usePdpStore.getState();
  const newMaterialOption: MaterialPublicDto | undefined = productAdmin.materialOptions.find(mat => mat.sku === material);
  const newLaminateOptions = newMaterialOption?.laminateOptions ?? productAdmin.laminateOptions ?? [];
  const newAdhesiveOptions = newMaterialOption?.adhesiveOptions ?? productAdmin.adhesiveOptions ?? [];
  clearDependentSelectionIfInvalid(store.laminate, newLaminateOptions, () => store.setLaminate(undefined));
  clearDependentSelectionIfInvalid(store.adhesive, newAdhesiveOptions, () => store.setAdhesive(undefined));
  store.setMaterial(material);
  store.clearValidationError("material");
  trackMaterialSelected(material);
}
export default function Material() {
  const $ = _c(32);
  const {
    product
  } = useProductBuilderContext();
  const {
    data: productAdmin
  } = useProduct(product.sku);
  const selectedMaterial = usePdpStore(_temp);
  const materialControlIdBase = `material-${product.sku}`;
  const materialLabelId = `${materialControlIdBase}-label`;
  const materialHelpDialogId = `${materialControlIdBase}-help-dialog`;
  const materialSelectContentId = `${materialControlIdBase}-select-content`;
  const contentfulMaterials = (product.materials ?? []).map(getEntry).filter(_temp2);
  const materialPublicNames = new Map(contentfulMaterials.map(_temp3));
  const microcopyFields = useProductMicrocopy();
  const materialHelper = microcopyFields?.materialHelper;
  const materialLabel = microcopyFields?.materialLabel ?? "Material";
  const materialValue = selectedMaterial ?? productAdmin?.defaultMaterial ?? "";
  let t0;
  if ($[0] !== materialValue) {
    t0 = material_1 => material_1.fields.sku === materialValue;
    $[0] = materialValue;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const selectedContentfulMaterial = contentfulMaterials.find(t0);
  const materialErrorId = useFieldErrorId("material");
  const descriptionId = selectedContentfulMaterial?.fields.shortDescription ? "material-description" : undefined;
  let t1;
  if ($[2] !== descriptionId || $[3] !== materialErrorId) {
    t1 = [descriptionId, materialErrorId].filter(Boolean).join(" ") || undefined;
    $[2] = descriptionId;
    $[3] = materialErrorId;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const materialDescribedBy = t1;
  if ((productAdmin?.materialOptions.length ?? 0) <= 1) {
    return null;
  }
  let t2;
  if ($[5] !== materialHelpDialogId || $[6] !== materialHelper || $[7] !== materialLabel || $[8] !== materialLabelId) {
    t2 = <OptionLabel id={materialLabelId} label={materialLabel} helper={materialHelper} helpDialogId={materialHelpDialogId} />;
    $[5] = materialHelpDialogId;
    $[6] = materialHelper;
    $[7] = materialLabel;
    $[8] = materialLabelId;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== productAdmin) {
    t3 = value => {
      if (!productAdmin) {
        return;
      }
      handleMaterialChange(value as ItemMaterial, productAdmin);
    };
    $[10] = productAdmin;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <SelectValue placeholder="-- Select Material --" />;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== materialDescribedBy || $[14] !== materialLabelId || $[15] !== materialSelectContentId) {
    t5 = <SelectTrigger aria-labelledby={materialLabelId} aria-describedby={materialDescribedBy} aria-controls={materialSelectContentId} className="w-full">{t4}</SelectTrigger>;
    $[13] = materialDescribedBy;
    $[14] = materialLabelId;
    $[15] = materialSelectContentId;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const t6 = productAdmin?.materialOptions.map(material_2 => {
    const materialName = materialPublicNames.get(material_2.sku) ?? material_2.displayName;
    return <SelectItem key={material_2.sku} value={material_2.sku}>{materialName}</SelectItem>;
  });
  let t7;
  if ($[17] !== materialSelectContentId || $[18] !== t6) {
    t7 = <SelectContent id={materialSelectContentId}>{t6}</SelectContent>;
    $[17] = materialSelectContentId;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== materialValue || $[21] !== t3 || $[22] !== t5 || $[23] !== t7) {
    t8 = <Select value={materialValue} onValueChange={t3}>{t5}{t7}</Select>;
    $[20] = materialValue;
    $[21] = t3;
    $[22] = t5;
    $[23] = t7;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== selectedContentfulMaterial) {
    t9 = selectedContentfulMaterial?.fields.shortDescription && <p id="material-description" className="text-sm text-muted-foreground">{selectedContentfulMaterial.fields.shortDescription}</p>;
    $[25] = selectedContentfulMaterial;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <ValidationError field="material" />;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== t2 || $[29] !== t8 || $[30] !== t9) {
    t11 = <div className="flex flex-col gap-1" data-validation-field="material">{t2}{t8}{t9}{t10}</div>;
    $[28] = t2;
    $[29] = t8;
    $[30] = t9;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  return t11;
}
function _temp3(material_0) {
  return [material_0.fields.sku, material_0.fields.publicNameOverride];
}
function _temp2(material) {
  return Boolean(material);
}
function _temp(state) {
  return state.material;
}